import { useEffect, type ReactNode } from 'react';
import { useBreadcrumbs } from '@deloitte-us-consulting/gp-sdk';
import { CornerTicks } from '../../theme/frontDoor/CornerTicks';
import { FdLink } from '../../theme/frontDoor/FdLink';
import { useScrollToTopOnArrival } from '../../theme/frontDoor/useScrollToTopOnArrival';
import { StackDiagram } from './StackDiagram';
import {
  ARCHITECTURE_SECTION,
  JOIN_SECTION,
  JOIN_STEPS,
  LAYERS_SECTION,
  PINNED_VERSIONS,
  STACK_DOCUMENT_TITLE,
  STACK_FACTS,
  STACK_FOOTER,
  STACK_HERO,
  STACK_LAYERS,
  VERSIONS_SECTION,
  type ChipIcon,
} from './stackContent';
import { STACK_ICONS } from './stackIcons';
import '../../theme/frontDoor/tokens.css';
import '../../theme/frontDoor/frontDoorDark.css';
import './stack.css';

const ids = {
  hero: 'gp-fd-stack-title',
  arch: 'gp-fd-stack-arch-title',
  layers: 'gp-fd-stack-layers-title',
  join: 'gp-fd-stack-join-title',
  versions: 'gp-fd-stack-versions-title',
} as const;

/** Renders `backticked` spans of a content string as inline code. */
function withCode(text: string): ReactNode {
  return text.split('`').map((part, i) => (i % 2 ? <code key={i}>{part}</code> : part));
}

/** A layer chip's mark: a Simple Icons brand mark, or a green ring where none exists. */
function ChipMark({ icon }: { icon: ChipIcon }) {
  if (icon === 'ring') return <span className="gp-fd-tech-mark gp-fd-tech-ring" aria-hidden="true" />;
  return (
    <svg className="gp-fd-tech-mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d={STACK_ICONS[icon]} />
    </svg>
  );
}

/**
 * The tech stack page (`/about/stack`): the "further detail" behind the AI Spine page's
 * Under the hood section, for builders and engineers. Ported from
 * docs/tech-stack-overview.html. Fetches nothing; all copy comes from stackContent.ts.
 */
export default function AboutStack() {
  // Same as AboutSpine: the SDK's useBreadcrumbs has no cleanup, so clear the previous
  // page's crumbs and set our own tab title. The back links do the navigating.
  useBreadcrumbs([]);
  useScrollToTopOnArrival();
  useEffect(() => {
    document.title = STACK_DOCUMENT_TITLE;
  }, []);

  return (
    <div className="gp-fd gp-fd-stack-page">
      {/* 1. Intro */}
      <section className="gp-fd-canvas gp-fd-stack-hero" aria-labelledby={ids.hero}>
        <span className="gp-fd-ring gp-fd-ring-a" aria-hidden="true" />
        <div className="gp-fd-inner gp-fd-rise">
          <FdLink to="/about" className="gp-fd-stack-back">
            {STACK_HERO.back}
          </FdLink>
          <p className="gp-fd-eyebrow">
            <span aria-hidden="true" />
            {STACK_HERO.eyebrow}
          </p>
          <h1 id={ids.hero} className="gp-fd-stack-title">
            {STACK_HERO.titleLead} <em>{STACK_HERO.titleEmphasis}</em>
            {STACK_HERO.titleTail}
          </h1>
          <p className="gp-fd-lede">{STACK_HERO.lede}</p>
          <ul className="gp-fd-stack-facts">
            {STACK_FACTS.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* 2. Architecture */}
      <section className="gp-fd-paper" aria-labelledby={ids.arch}>
        <div className="gp-fd-inner">
          <h2 id={ids.arch} className="gp-fd-section-title">
            {ARCHITECTURE_SECTION.title}
          </h2>
          <p className="gp-fd-sub">{ARCHITECTURE_SECTION.sub}</p>
          {/* Focusable so keyboard users can scroll it sideways on narrow screens. */}
          <div className="gp-fd-diagram-frame">
            <CornerTicks />
            <div className="gp-fd-diagram" tabIndex={0} role="region" aria-label={ARCHITECTURE_SECTION.diagramLabel}>
              <StackDiagram />
            </div>
          </div>
          <p className="gp-fd-diagram-hint">{ARCHITECTURE_SECTION.scrollHint}</p>
        </div>
      </section>

      {/* 3. The six layers */}
      <section className="gp-fd-paper" aria-labelledby={ids.layers}>
        <div className="gp-fd-inner">
          <h2 id={ids.layers} className="gp-fd-section-title">
            {LAYERS_SECTION.title}
          </h2>
          <p className="gp-fd-sub">{LAYERS_SECTION.sub}</p>
          <ul className="gp-fd-stack-layers" aria-labelledby={ids.layers}>
            {STACK_LAYERS.map((layer) => (
              <li
                key={layer.id}
                className={layer.id === 'ai' ? 'gp-fd-stack-layer gp-fd-stack-layer-ai' : 'gp-fd-stack-layer'}
              >
                <h3>{layer.title}</h3>
                <p>{layer.blurb}</p>
                <ul className="gp-fd-tech-list" aria-label={layer.title}>
                  {layer.chips.map((chip) => (
                    <li key={chip.name} className="gp-fd-tech">
                      <ChipMark icon={chip.icon} />
                      <div>
                        <b>{chip.name}</b>
                        <span className="gp-fd-tech-detail">{withCode(chip.detail)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4. How an app joins, and the pinned versions */}
      <section className="gp-fd-paper">
        <div className="gp-fd-inner gp-fd-stack-lower">
          <div aria-labelledby={ids.join} role="region">
            <h2 id={ids.join} className="gp-fd-section-title">
              {JOIN_SECTION.title}
            </h2>
            <p className="gp-fd-sub">{JOIN_SECTION.sub}</p>
            <ol className="gp-fd-join">
              {JOIN_STEPS.map((step, i) => (
                <li key={step.title}>
                  <span className="gp-fd-join-n" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <b>{step.title}</b>
                    <span className="gp-fd-join-body">{withCode(step.body)}</span>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div aria-labelledby={ids.versions} role="region">
            <h2 id={ids.versions} className="gp-fd-section-title">
              {VERSIONS_SECTION.title}
            </h2>
            <p className="gp-fd-sub">{VERSIONS_SECTION.sub}</p>
            <table className="gp-fd-pins" aria-labelledby={ids.versions}>
              <thead className="gp-fd-sr-only">
                <tr>
                  <th scope="col">{VERSIONS_SECTION.packageHeader}</th>
                  <th scope="col">{VERSIONS_SECTION.versionHeader}</th>
                </tr>
              </thead>
              <tbody>
                {PINNED_VERSIONS.map((pin) => (
                  <tr key={pin.packages.join()}>
                    <th scope="row">{pin.packages.join(' / ')}</th>
                    <td>{pin.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="gp-fd-pins-note">{withCode(VERSIONS_SECTION.note)}</p>
          </div>
        </div>
      </section>

      {/* 5. Footer */}
      <section className="gp-fd-paper gp-fd-stack-footer">
        <div className="gp-fd-inner">
          <p>{STACK_FOOTER.source}</p>
          <FdLink to="/about" className="gp-fd-button gp-fd-button-outline">
            {STACK_FOOTER.back}
          </FdLink>
        </div>
      </section>
    </div>
  );
}
